import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal } from 'antd';
import { CloseOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { HoverCardData, MapMarker, ExtraMapMarker } from '../../types/map';
import MapContainerComponent from './MapContainerComponent';
import './MapWrapper.less';
import MapStaticPreview from './components/MapStaticPreview';
import SpinLoader from '@skygroup/shared/components/spin-loader/SpinLoader';
import { OptionalFilter } from '../SideFilters/interface';
import { RootState } from '../../store/store';
import { useSelector } from 'react-redux';

interface MapWrapperProps {
  data: any[];
  paginatedData?: any[];
  optional_filters?: OptionalFilter[];
  title: string;
  renderHoverCard?: (data: HoverCardData) => React.ReactNode;
  // Modal mode props
  isOpen?: boolean;
  onClose?: () => void;
  // Inline mode props
  inline?: boolean;
  buttonText?: string;
  buttonSize?: 'small' | 'middle' | 'large';
  buttonType?: 'default' | 'primary' | 'dashed' | 'link' | 'text';
  showButton?: boolean;
  isFullyLoaded?: boolean;
  isLoading?: boolean;
  // type?: 'hotel' | 'attraction';
  selectedMarkerId?: string;
  is_dp?: boolean;
  preview_type?: 'small' | 'large';
  children?: React.ReactNode;
  hasNameSearch?: boolean;
  searchHotelsByGeo?: boolean;
  handleSelectMarker?: (id: string) => void;
  initialZoom?: number;
  previewInitZoom?: number;
  localSideFilters?: Record<string, any> | null;
  setLocalSideFilters?: (f: Record<string, any>) => void;
  showMarkerNameLabel?: boolean;
  extraMarkers?: ExtraMapMarker[];
  additionalPrice?: number;
  onGeoRefetchSuccess?: (
    hotels: any[],
    optionalFilters?: OptionalFilter[]
  ) => void;
}

const MapWrapper: React.FC<MapWrapperProps> = ({
  data,
  paginatedData,
  optional_filters,
  title,
  renderHoverCard,
  isOpen,
  onClose,
  inline = false,
  buttonText,
  buttonSize = 'middle',
  buttonType = 'primary',
  showButton = true,
  isFullyLoaded = false,
  isLoading = false,
  selectedMarkerId,
  is_dp = false,
  preview_type = 'large',
  children,
  hasNameSearch,
  searchHotelsByGeo = false,
  handleSelectMarker,
  initialZoom,
  previewInitZoom,
  localSideFilters,
  setLocalSideFilters,
  showMarkerNameLabel = false,
  extraMarkers,
  additionalPrice,
  onGeoRefetchSuccess,
}) => {
  const { general: t, searchDatePicker: t_searchDatePicker } = useSelector(
    (data: RootState) => data.config.translations
  );
  const [internalModalOpen, setInternalModalOpen] = useState(false);
  const mapContentRef = useRef<HTMLDivElement>(null);
  // Determine if we're in controlled modal mode or internal button mode
  const isControlledModal = isOpen !== undefined && onClose !== undefined;
  const modalOpen = isControlledModal ? isOpen : internalModalOpen;

  const handleModalClose = useCallback(() => {
    // setHoverCardData(null);
    if (isControlledModal && onClose) {
      onClose();
    } else {
      setInternalModalOpen(false);
    }
  }, [isControlledModal, onClose]);

  const handleButtonClick = useCallback(() => {
    if (isControlledModal && onClose !== undefined) {
      // This shouldn't happen in controlled mode, but just in case
      return;
    }
    setInternalModalOpen(true);
  }, [isControlledModal]);

  const [calculatedCenter, setCalculatedCenter] = useState(false);
  const [initPosition, setInitPosition] = useState<{
    lat: number;
    lng: number;
  }>(
    data?.length
      ? { lat: data[0].position[0], lng: data[0].position[1] }
      : { lat: 0, lng: 0 }
  );

  useEffect(() => {
    if (!calculatedCenter && data?.length) {
      // calculate center location of all hotels
      const meanLat =
        data.reduce((sum: number, item: any) => sum + item.position[0], 0) /
        data.length;
      const meanLng =
        data.reduce((sum: number, item: any) => sum + item.position[1], 0) /
        data.length;
      setInitPosition({
        lat: meanLat || data[0].position[0],
        lng: meanLng || data[0].position[1],
      });
      setCalculatedCenter(true);
    }
  }, [data, calculatedCenter]);

  // useEffect(() => {
  //   if (inline && selectedMarkerId) {
  //     mapContentRef.current?.scrollIntoView({ behavior: 'smooth' });
  //   }
  // }, [inline, selectedMarkerId]);

  // Render the map component
  const renderMap = () => (
    <SpinLoader isLoading={isLoading}>
      <div
        ref={mapContentRef}
        className={`map-content ${optional_filters?.length ? 'has-sidebar' : ''}`}
      >
        <MapContainerComponent
          initialMarkersArr={data}
          optionalFilters={optional_filters}
          isFullyLoaded={isFullyLoaded}
          initPosition={initPosition}
          renderHoverCard={renderHoverCard}
          selectedMarkerId={selectedMarkerId}
          is_dp={is_dp}
          hasNameSearch={hasNameSearch}
          searchHotelsByGeo={searchHotelsByGeo}
          handleSelectMarker={handleSelectMarker}
          initialZoom={initialZoom}
          localSideFilters={localSideFilters}
          setLocalSideFilters={setLocalSideFilters}
          showMarkerNameLabel={showMarkerNameLabel}
          extraMarkers={extraMarkers}
          additionalPrice={additionalPrice}
          onGeoRefetchSuccess={onGeoRefetchSuccess}
        >
          {children}
        </MapContainerComponent>
      </div>
    </SpinLoader>
  );

  // Inline mode - render map directly
  if (inline) {
    return <div className="map-inline-container">{renderMap()}</div>;
  }

  // Modal mode
  return (
    <>
      {/* Show button only in uncontrolled mode or when explicitly requested */}
      {data.length > 0 && !isControlledModal && showButton && (
        <MapStaticPreview
          initPosition={initPosition}
          previewInitZoom={previewInitZoom}
          handleButtonClick={handleButtonClick}
          buttonText={buttonText || t_searchDatePicker.mapView}
          buttonSize={buttonSize}
          buttonType={buttonType}
          preview_type={preview_type}
        />
      )}

      <Modal
        open={modalOpen}
        onCancel={handleModalClose}
        closable
        footer={null}
        width="100vw"
        style={{ height: '100vh' }}
        centered
        className="map-modal"
        destroyOnClose={false}
        closeIcon={
          <div className="map-modal-close-icon-custom">
            <span>{`${t.close} ${t_searchDatePicker.mapView}`}</span>
            <CloseOutlined />
          </div>
        }
      >
        {modalOpen && <div className="map-modal-content">{renderMap()}</div>}
      </Modal>
    </>
  );
};

export default MapWrapper;
